import {
  FormControl,
  InputAdornment,
  IconButton,
  TextField as MUITextField,
  makeStyles,
  FormLabel,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { BoxSkeleton } from "app/components/modules/Skeletons";
import { INPUT_HEIGHT } from "containers/themes/attributes";
import { globalStyles } from "containers/themes/globalClasses";
import clsx from "clsx";
import { isEqual } from "lodash";
import React, { forwardRef, useState } from "react";

export const TextField = React.memo(
  forwardRef((props, ref) => {
    const {
      name,
      error = false,
      type = "text",
      inputLabel,
      label,
      value,
      onChange,
      readOnly = false,
      endAdornment,
      endAdornmentStyle,
      startAdornment,
      className,
      inputClassName,
      onBlur,
      multiline,
      size,
      maxLength,
      min,
      max,
      disabled,
      loading = false,
      required,
      autoComplete = "off",
      ...rest
    } = props;
    const classes = useStyles({ multiline });
    const [showPassword, setShowPassword] = useState(false);

    const handleTogglePassword = () => {
      setShowPassword((prevShowPassword) => !prevShowPassword);
    };

    const handleChange = (e) => {
      let val = e.target.value;
      if (min !== undefined && val < min) val = min;
      if (max !== undefined && val > max) val = max;
      onChange?.({ ...e, target: { ...e.target, value: val, name } });
    };
    const isPassword = type === "password";
    const inputType = isPassword ? (showPassword ? "text" : "password") : type;
    return (
      <FormControl
        error={Boolean(error)}
        required={required}
        fullWidth
        className={clsx(classes.formControl, className, {
          [classes.readOnly]: readOnly,
        })}
      >
        {label && <FormLabel>{label}</FormLabel>}
        {loading ? (
          <BoxSkeleton
            square={false}
            rectSize={{ width: "100%", height: INPUT_HEIGHT }}
          />
        ) : (
          <MUITextField
            inputRef={ref}
            name={name}
            label={inputLabel}
            variant="outlined"
            type={inputType}
            value={value ?? ""}
            multiline={multiline}
            onChange={handleChange}
            onBlur={onBlur}
            disabled={disabled}
            InputProps={{
              classes: {
                root: classes.inputRoot,
                input: classes.input,
                multiline: classes.multilineRoot,
                inputMultiline: classes.multilineInput,
              },
              inputProps: {
                className: inputClassName,
                maxLength,
                min,
                max,
              },
              startAdornment: startAdornment ? (
                <InputAdornment position="start">
                  {startAdornment}
                </InputAdornment>
              ) : undefined,
              endAdornment:
                endAdornment || isPassword ? (
                  <InputAdornment
                    position="end"
                    style={{ ...endAdornmentStyle }}
                  >
                    {isPassword && (
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleTogglePassword}
                        edge="end"
                      >
                        {showPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    )}
                    {endAdornment}
                  </InputAdornment>
                ) : undefined,
            }}
            InputLabelProps={{
              ...((value || type === "time") && { shrink: true }),
            }}
            error={Boolean(error)}
            helperText={error ?? undefined}
            FormHelperTextProps={{ style: { marginLeft: 0, marginTop: 0 } }}
            autoComplete={autoComplete}
            {...rest}
          />
        )}
      </FormControl>
    );
  }),
  (prev, next) => isEqual(prev, next)
);
const useStyles = makeStyles(
  (theme) => ({
    formControl: {
      ...globalStyles(theme).formControl,
      height: "auto",
    },
    inputRoot: {
      height: ({ multiline }) => (multiline ? "auto" : INPUT_HEIGHT),
      overflow: ({ multiline }) => (multiline ? "visible" : "hidden"),
      alignItems: ({ multiline }) => (multiline ? "flex-start" : "center"),
    },
    input: {
      height: INPUT_HEIGHT,
      padding: "0px 15px",
      paddingLeft: theme.spacing(1),
      fontSize: 14,
      "&::placeholder": {
        color: theme.palette.grey.main,
        opacity: 1,
      },
    },
    multilineRoot: {
      padding: theme.spacing(1),
    },
    multilineInput: {
      height: "auto",
      minHeight: "auto",
      padding: 0,
      lineHeight: 1.5,
      "&::placeholder": {
        color: theme.palette.grey.main,
        opacity: 1,
      },
    },
    readOnly: globalStyles(theme).readOnly,
  }),
  { name: "CommonTextField" }
);
